import { useRef, useState } from 'react';
import { DialogActions, DialogContent } from '@mui/material';
import { useDispatch } from 'react-redux';

import { NotificationTypeEnum } from '@/types';
import { adminProvider } from '@/providers';
import { appSetNotification } from '@/store';

import { MyButton } from '../../uiComponents/MyButton/MyButton';
import { MyDialog } from '../../uiComponents/MyDialog/MyDialog';
import { getPublicationTimerResetResult } from '../../Admin/publicationTimerResetMessages';

export const ResetPublicationTimerAction = () => {
  const dispatch = useDispatch();
  const requestInFlightRef = useRef(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updatedCount, setUpdatedCount] = useState<number | null>(null);

  const handleOpen = () => {
    setUpdatedCount(null);
    setIsConfirmationOpen(true);
  };

  const handleClose = () => {
    if (!requestInFlightRef.current) {
      setIsConfirmationOpen(false);
      setUpdatedCount(null);
    }
  };

  const handleConfirm = async () => {
    if (requestInFlightRef.current) return;

    requestInFlightRef.current = true;
    setIsSubmitting(true);

    try {
      const { data, message, ok, status } = await adminProvider.resetPropertyAdPublicationTimer();

      if (!ok || !data) {
        dispatch(
          appSetNotification(
            NotificationTypeEnum.Error,
            message || 'Unable to extend published listings',
            status
          )
        );
        return;
      }

      setUpdatedCount(data.updated);
    } catch (error) {
      dispatch(
        appSetNotification(
          NotificationTypeEnum.Error,
          error instanceof Error ? error.message : 'Unable to extend published listings'
        )
      );
    } finally {
      requestInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  const result = updatedCount === null ? null : getPublicationTimerResetResult(updatedCount, 'all');

  return (
    <>
      <MyButton
        data={{
          buttonName: isSubmitting ? 'Extending...' : 'Extend all by 90 days',
          buttonType: 'button',
          customWidth: 'auto',
          size: 'medium',
          styleType: 'cancel',
          variant: 'outlined',
        }}
        disabled={isSubmitting}
        onClick={handleOpen}
      />
      <MyDialog
        dialogTitle={result?.title || 'Extend all published listings?'}
        onClose={handleClose}
        open={isConfirmationOpen}
        width='600'
      >
        <DialogContent sx={{ paddingX: 0, textAlign: 'center' }}>
          {result?.text ||
            'All currently published listings will receive a new 90-day publication period.'}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 2, justifyContent: 'center', paddingX: 0 }}>
          {result ? (
            <MyButton
              data={{
                buttonName: 'Done',
                buttonType: 'button',
                customWidth: '190px',
                variant: 'contained',
              }}
              onClick={handleClose}
            />
          ) : (
            <>
              <MyButton
                data={{
                  buttonName: 'Cancel',
                  buttonType: 'button',
                  customWidth: '190px',
                  styleType: 'cancel',
                  variant: 'outlined',
                }}
                disabled={isSubmitting}
                onClick={handleClose}
              />
              <MyButton
                data={{
                  buttonName: isSubmitting ? 'Extending...' : 'Extend by 90 days',
                  buttonType: 'button',
                  customWidth: '190px',
                  variant: 'contained',
                }}
                disabled={isSubmitting}
                onClick={handleConfirm}
              />
            </>
          )}
        </DialogActions>
      </MyDialog>
    </>
  );
};
